import { Group, Layer, Stage } from 'react-konva'
import { VisualShape } from 'app/frontend/types/custom/VisualPlan'
import { FALLBACK_ASPECT } from '../constants'
import ShapeRenderer from '../shapes/ShapeRenderer'
import { measureTextBoxHeightPx } from '../shapes/textLayout'

const VIRT_W = 200
const THUMB = 22
const PADDING = 3
const THUMB_STROKE = 1.5

type Bounds = { minX: number; minY: number; maxX: number; maxY: number }

const shapeBounds = (shape: VisualShape, virtH: number): Bounds => {
  if (
    shape.type === 'rect' ||
    shape.type === 'triangle' ||
    shape.type === 'icon'
  ) {
    const cx = shape.x * VIRT_W
    const cy = shape.y * virtH
    const w = shape.width * VIRT_W
    const h = shape.height * virtH
    return {
      minX: cx - w / 2,
      minY: cy - h / 2,
      maxX: cx + w / 2,
      maxY: cy + h / 2,
    }
  }
  if (shape.type === 'text') {
    const cx = shape.x * VIRT_W
    const cy = shape.y * virtH
    const w = shape.width * VIRT_W
    const h = measureTextBoxHeightPx(shape.text, w, shape.font_size * VIRT_W)
    return {
      minX: cx - w / 2,
      minY: cy - h / 2,
      maxX: cx + w / 2,
      maxY: cy + h / 2,
    }
  }
  if (shape.type === 'circle') {
    const cx = shape.x * VIRT_W
    const cy = shape.y * virtH
    const rx = shape.radius_x * VIRT_W
    const ry = shape.radius_y * VIRT_W
    return { minX: cx - rx, minY: cy - ry, maxX: cx + rx, maxY: cy + ry }
  }
  const cx = shape.x * VIRT_W
  const cy = shape.y * virtH
  const xs = shape.points
    .filter((_, i) => i % 2 === 0)
    .map((p) => cx + p * VIRT_W)
  const ys = shape.points
    .filter((_, i) => i % 2 !== 0)
    .map((p) => cy + p * virtH)
  return {
    minX: Math.min(...xs),
    minY: Math.min(...ys),
    maxX: Math.max(...xs),
    maxY: Math.max(...ys),
  }
}

type Props = {
  shape: VisualShape
  aspect?: number
}

const ShapeThumbnail = ({ shape, aspect = FALLBACK_ASPECT }: Props) => {
  const virtH = VIRT_W * aspect
  const bounds = shapeBounds(shape, virtH)
  const bboxW = Math.max(1, bounds.maxX - bounds.minX)
  const bboxH = Math.max(1, bounds.maxY - bounds.minY)
  const available = THUMB - 2 * PADDING
  const scale = Math.min(available / bboxW, available / bboxH)
  const scaledW = bboxW * scale
  const scaledH = bboxH * scale
  const groupX = PADDING + (available - scaledW) / 2 - bounds.minX * scale
  const groupY = PADDING + (available - scaledH) / 2 - bounds.minY * scale

  const thumbnailShape = { ...shape, stroke_width: THUMB_STROKE }

  return (
    <div
      className='shrink-0 overflow-hidden rounded-md bg-muted'
      style={{ width: THUMB, height: THUMB }}
    >
      <Stage width={THUMB} height={THUMB}>
        <Layer>
          <Group x={groupX} y={groupY} scaleX={scale} scaleY={scale}>
            <ShapeRenderer
              shape={thumbnailShape}
              width={VIRT_W}
              height={virtH}
              showEntityMarkers={false}
            />
          </Group>
        </Layer>
      </Stage>
    </div>
  )
}

export default ShapeThumbnail
